import { useState, useRef, useEffect } from 'react';
import type { AppLockInstance } from '@ascend/applock';
import {
  Lock,
  KeyRound,
  Trash2,
  ChevronDown,
  TriangleAlert,
} from 'lucide-react';

interface LockMenuProps {
  lock: AppLockInstance;
}

export function LockMenu({ lock }: LockMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'menu' | 'change' | 'remove'>('menu');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [removePw, setRemovePw] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setView('menu');
        setError('');
        setSuccess('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleLock = () => {
    lock.lock('manual');
    setOpen(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPw || !newPw || busy) return;
    if (newPw.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await lock.changePassword(currentPw, newPw);
      setSuccess('Password changed successfully.');
      setCurrentPw('');
      setNewPw('');
      setView('menu');
    } catch {
      setError('Current password is incorrect.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!removePw || busy) return;
    setError('');
    setBusy(true);
    try {
      await lock.removePassword(removePw);
      setRemovePw('');
      setOpen(false);
      setView('menu');
    } catch {
      setError('Password is incorrect.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lock-menu" ref={ref}>
      <button
        type="button"
        className="lock-menu-trigger"
        onClick={() => setOpen(!open)}
        title="App lock settings"
        aria-label="App lock settings"
        aria-expanded={open}
        data-testid="lock-menu-trigger"
      >
        <Lock size={18} strokeWidth={2} />
        <ChevronDown size={14} strokeWidth={2} />
      </button>

      {open && (
        <div className="lock-menu-dropdown" role="menu">
          {view === 'menu' && (
            <>
              <button
                type="button"
                className="lock-menu-item"
                onClick={handleLock}
                role="menuitem"
              >
                <Lock size={16} strokeWidth={2} />
                Lock Now
              </button>
              <button
                type="button"
                className="lock-menu-item"
                onClick={() => {
                  setView('change');
                  setError('');
                  setSuccess('');
                }}
                role="menuitem"
              >
                <KeyRound size={16} strokeWidth={2} />
                Change Password
              </button>
              <button
                type="button"
                className="lock-menu-item lock-menu-item-danger"
                onClick={() => {
                  setView('remove');
                  setError('');
                  setSuccess('');
                }}
                role="menuitem"
              >
                <Trash2 size={16} strokeWidth={2} />
                Remove Password
              </button>
              {success && <p className="lock-menu-success">{success}</p>}
            </>
          )}

          {view === 'change' && (
            <form className="lock-menu-form" onSubmit={handleChangePassword}>
              <button
                type="button"
                className="lock-menu-back"
                onClick={() => {
                  setView('menu');
                  setCurrentPw('');
                  setNewPw('');
                  setError('');
                }}
              >
                Back
              </button>
              <h3 className="lock-menu-heading">Change Password</h3>
              <div className="applock-field">
                <label htmlFor="lock-change-current">Current password</label>
                <input
                  id="lock-change-current"
                  type="password"
                  autoComplete="current-password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </div>
              <div className="applock-field">
                <label htmlFor="lock-change-new">New password</label>
                <input
                  id="lock-change-new"
                  type="password"
                  autoComplete="new-password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  disabled={busy}
                />
              </div>
              {error && <p className="applock-error">{error}</p>}
              <button
                type="submit"
                className="applock-btn applock-btn-primary"
                disabled={busy || !currentPw || !newPw}
              >
                {busy ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          )}

          {view === 'remove' && (
            <form className="lock-menu-form" onSubmit={handleRemovePassword}>
              <button
                type="button"
                className="lock-menu-back"
                onClick={() => {
              setView('menu');
              setRemovePw('');
              setRemoveConfirm(false);
              setError('');
                }}
              >
                Back
              </button>
              <h3 className="lock-menu-heading">Remove Password</h3>
              <div className="applock-warning">
                <TriangleAlert size={18} strokeWidth={2} />
                <span>
                  Removing the password decrypts all data back to cleartext.
                  Anyone with device access can read it.
                </span>
              </div>
              <div className="applock-field">
                <label htmlFor="lock-remove-pw">Current password</label>
                <input
                  id="lock-remove-pw"
                  type="password"
                  autoComplete="current-password"
                  value={removePw}
                  onChange={(e) => setRemovePw(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </div>
              <label className="applock-confirm" htmlFor="lock-remove-confirm">
                <input
                  id="lock-remove-confirm"
                  type="checkbox"
                  checked={removeConfirm}
                  onChange={(e) => setRemoveConfirm(e.target.checked)}
                  disabled={busy}
                />
                I understand this decrypts all stored data back to cleartext and removes password protection.
              </label>
              {error && <p className="applock-error">{error}</p>}
              <button
                type="submit"
                className="applock-btn applock-btn-danger"
                disabled={busy || !removePw || !removeConfirm}
              >
                {busy ? 'Removing...' : 'Remove Password'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
